import { Injectable } from '@angular/core';
import { GridService } from '../shared/grid/grid.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { RiskAssessment } from '../models/risk-assessment.model';
import {
  RiskRegisterAgentColumnDeleteResponse,
  RiskRegisterAgentColumnResponse,
  RiskRegisterSegPageQuery,
  RiskRegisterSegPageResponse,
  RiskRegisterSegRef,
} from '../models/risk-register-seg-page.model';
import { RiskAssessmentSummary } from '../models/risk-assessment-summary';
import { RiskAssessmentScopeOptionsResponse, RiskAssessmentScopeUsedResponse } from '../models/risk-assessment-scope.model';
import { RiskAssessmentProgress } from '../models/risk-assessment-progress.model';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class RiskAssessmentService extends GridService<RiskAssessment> {

  constructor(http: HttpClient) {
    super('RiskAssessment', http);
  }

  getRiskAssessmentSummary(body: unknown): Observable<RiskAssessmentSummary[]>{
    return this.http.post<RiskAssessmentSummary[]>(this.getApiUrl(`${this.controllerName}/GetRiskAssessmentSummary`), body);
  }

  recalculate(assessmentId: string): Observable<void> {
    return this.http.post<void>(this.getApiUrl(`${this.controllerName}/${assessmentId}/recalculate`), {});
  }

  getScopeOptions(organizationLevelId: string): Observable<RiskAssessmentScopeOptionsResponse> {
    return this.http.get<RiskAssessmentScopeOptionsResponse>(
      this.getApiUrl(`${this.controllerName}/scope-options?organizationLevelId=${organizationLevelId}`)
    );
  }

  getScopeUsed(riskAssessmentId: string): Observable<RiskAssessmentScopeUsedResponse> {
    return this.http.get<RiskAssessmentScopeUsedResponse>(
      this.getApiUrl(`${this.controllerName}/${riskAssessmentId}/scope-used`)
    );
  }

  getProgress(assessmentId: string): Observable<RiskAssessmentProgress> {
    return this.http.get<RiskAssessmentProgress>(this.getApiUrl(`${this.controllerName}/${assessmentId}/progress`));
  }

  getRiskRegisterPage(assessmentId: string, query: RiskRegisterSegPageQuery): Observable<RiskRegisterSegPageResponse> {
    const params = this.toParams({
      page: query.page,
      pageSize: query.pageSize,
      search: query.search,
      filterBy: query.filterBy,
      groupBy: query.groupBy,
      sortBy: query.sortBy,
      sortAgentId: query.sortAgentId,
      sortDirection: query.sortDirection,
      viewMode: query.viewMode,
    });
    return this.http.get<RiskRegisterSegPageResponse>(this.getApiUrl(`risk-assessments/${assessmentId}/risk-registers`), { params });
  }

  getRiskRegisterAgents(assessmentId: string, search: string, filterBy: string): Observable<RiskRegisterAgentColumnResponse[]> {
    const params = this.toParams({ search, filterBy });
    return this.http.get<RiskRegisterAgentColumnResponse[]>(this.getApiUrl(`risk-assessments/${assessmentId}/risk-registers/agents`), { params });
  }

  getAgentColumnRegisters(assessmentId: string, agentId: string): Observable<RiskRegisterSegRef[]> {
    return this.http.get<RiskRegisterSegRef[]>(this.getApiUrl(`risk-assessments/${assessmentId}/risk-registers/agents/${agentId}`));
  }

  deleteAgentColumnRegisters(assessmentId: string, agentId: string): Observable<RiskRegisterAgentColumnDeleteResponse> {
    return this.http.delete<RiskRegisterAgentColumnDeleteResponse>(this.getApiUrl(`risk-assessments/${assessmentId}/risk-registers/agents/${agentId}`));
  }

  saveAgentColumnOrder(assessmentId: string, agentIds: string[]): Observable<void> {
    return this.http.put<void>(this.getApiUrl(`risk-assessments/${assessmentId}/agent-column-order`), { agentIds });
  }

  private toParams(values: Record<string, string | number | undefined>): HttpParams {
    let params = new HttpParams();
    Object.entries(values).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params = params.set(key, value);
      }
    });
    return params;
  }
}
